import React from 'react';
import { defineMessages, useIntl } from 'react-intl';
import { Text } from '@lifeomic/chroma-react/components/Text';
import { Button } from '@lifeomic/chroma-react/components/Button';
import { Modal } from '@lifeomic/chroma-react/components/Modal';
import { Chat, MemberThread } from './Chat';
import { makeStyles, Theme } from '@lifeomic/chroma-react/styles';
import { Box } from '@lifeomic/chroma-react/components/Box';
import clsx from 'clsx';
import { Divider } from '@mui/material';
import { ChatSummary } from './ChatSummary';
import { MEMBERS_AGENT_USERNAME } from '../constants';
import {
  useActiveProjectId,
  useFeatures,
  useNotify,
} from '@lifeomic/phc-web-toolkit/src/hooks';
import { apiHooks } from '@lifeomic/phc-web-toolkit/src/hooks/useRestAPIHooks';

const messages = defineMessages({
  summary: {
    id: 'agentChat.memberChatHistory.summary',
    defaultMessage: 'Summary',
  },
  history: {
    id: 'agentChat.memberChatHistory.history',
    defaultMessage: 'History',
  },
  resetMemory: {
    id: 'agentChat.memberChatHistory.resetMemory',
    defaultMessage: 'Reset memory',
  },
  resetTitle: {
    id: 'agentChat.memberChatHistory.resetTitle',
    defaultMessage: "Reset Zori's memory of this chat?",
  },
  resetBody: {
    id: 'agentChat.memberChatHistory.resetBody',
    defaultMessage:
      "Zori keeps a running summary of this conversation. Facts it recorded stay in that summary even after they change, so a member can keep hearing an old care team. Resetting clears the summary; Zori rebuilds from the recent messages below and looks anything else up again. The conversation itself isn't deleted.",
  },
  resetConfirm: {
    id: 'agentChat.memberChatHistory.resetConfirm',
    defaultMessage: 'Reset memory',
  },
  resetCancel: {
    id: 'agentChat.memberChatHistory.resetCancel',
    defaultMessage: 'Cancel',
  },
  resetSuccess: {
    id: 'agentChat.memberChatHistory.resetSuccess',
    defaultMessage: "Zori's memory of this chat was reset.",
  },
  resetNoop: {
    id: 'agentChat.memberChatHistory.resetNoop',
    defaultMessage: 'Zori had no stored memory of this chat to reset.',
  },
  resetFailed: {
    id: 'agentChat.memberChatHistory.resetFailed',
    defaultMessage: "Couldn't reset Zori's memory of this chat.",
  },
});

const useStyles = makeStyles((theme: Theme) => ({
  panels: {
    flex: 1,
    maxHeight: '100%',
  },
  panel: {
    flex: 1,
    flexShrink: 10,
    overflowY: 'auto',
    minHeight: theme.pxToRem(200),
  },
  historyPanel: {
    flex: 3,
    paddingBottom: theme.pxToRem(48),
  },
  header: {
    background: theme.palette.background.paper,
    borderBottom: `1px solid ${theme.palette.divider}`,
    color: theme.palette.text.secondary,
    letterSpacing: theme.pxToRem(1.5),
    padding: theme.spacing(0.5, 2),
    position: 'sticky',
    top: 0,
    textTransform: 'uppercase',
    zIndex: 3,
  },
  headerRow: {
    alignItems: 'center',
    background: theme.palette.background.paper,
    borderBottom: `1px solid ${theme.palette.divider}`,
    justifyContent: 'space-between',
    paddingRight: theme.spacing(1),
    position: 'sticky',
    top: 0,
    zIndex: 3,
  },
  headerRowTitle: {
    borderBottom: 'none',
    flex: 1,
    position: 'static',
  },
  body: {
    flexDirection: 'column',
    height: '100%',
    padding: theme.spacing(2),
    '& p + *': {
      marginTop: theme.spacing(2),
    },
  },
  historyBody: {
    flex: 1,
    padding: theme.spacing(0, 0, 2, 0),
  },
}));

export const MemberChatHistory = ({
  patient,
  highlightMessageId,
  highlightNonce,
}: {
  patient: fhir.Patient;
  /** Forum post id of a chat message to scroll to and briefly highlight. */
  highlightMessageId?: string;
  /** Bumps to re-trigger scroll/highlight for the same message id. */
  highlightNonce?: number;
}) => {
  const { formatMessage } = useIntl();
  const classes = useStyles({});
  const hasFeature = useFeatures();
  const [notify] = useNotify();
  const projectId = useActiveProjectId();
  const [thread, setThread] = React.useState<MemberThread>();
  // A thread only belongs to the member it was loaded for. Switching members
  // leaves the previous member's thread reported for a moment, and pairing it
  // with whoever is in view now would reset the wrong member's memory.
  const threadId =
    thread && thread.subjectId === patient.id ? thread.threadId : undefined;
  const [confirmingReset, setConfirmingReset] = React.useState(false);
  const resetMutation = apiHooks.useAPIMutation(
    'DELETE /v1/agents-v2/member-chat/session',
  );
  const canResetMemory = hasFeature('fountainLifePowerUser');

  const resetMemory = () => {
    if (!threadId || !patient.id) return;
    resetMutation.mutate(
      { subject_id: patient.id, project_id: projectId, session_id: threadId },
      {
        onSuccess: ({ deleted }) => {
          setConfirmingReset(false);
          notify(
            formatMessage(deleted ? messages.resetSuccess : messages.resetNoop),
            deleted ? 'success' : 'info',
          );
        },
        onError: () => {
          notify(formatMessage(messages.resetFailed), 'error');
        },
      },
    );
  };

  // Until the first page of posts arrives there is no thread id to reset, and
  // a member who has never messaged Zori has no thread at all.
  const resetControl =
    canResetMemory && threadId ? (
      <Button variant="text" onClick={() => setConfirmingReset(true)}>
        {formatMessage(messages.resetMemory)}
      </Button>
    ) : null;

  return (
    <Box className={classes.panels} direction="column">
      <Box className={classes.panel} direction="column">
        <Text size="caption" weight="bold" className={classes.header}>
          {formatMessage(messages.summary)}
        </Text>
        <Box className={classes.body}>
          <ChatSummary patient={patient} />
        </Box>
      </Box>
      <Divider />
      <Box
        className={clsx(classes.panel, classes.historyPanel)}
        direction="column"
      >
        <Box className={classes.headerRow}>
          <Text
            size="caption"
            weight="bold"
            className={clsx(classes.header, classes.headerRowTitle)}
          >
            {formatMessage(messages.history)}
          </Text>
          {resetControl}
        </Box>
        <Box className={clsx(classes.body, classes.historyBody)}>
          <Chat
            patient={patient}
            isMemberChatHistory={true}
            agentUsername={MEMBERS_AGENT_USERNAME}
            onMemberThread={setThread}
            highlightMessageId={highlightMessageId}
            highlightNonce={highlightNonce}
          />
        </Box>
      </Box>
      <Modal
        isOpen={confirmingReset}
        onDismiss={() => setConfirmingReset(false)}
        title={formatMessage(messages.resetTitle)}
        actions={
          <>
            <Button variant="text" onClick={() => setConfirmingReset(false)}>
              {formatMessage(messages.resetCancel)}
            </Button>
            <Button
              variant="contained"
              disabled={resetMutation.isLoading}
              onClick={resetMemory}
            >
              {formatMessage(messages.resetConfirm)}
            </Button>
          </>
        }
      >
        <Text size="subbody">{formatMessage(messages.resetBody)}</Text>
      </Modal>
    </Box>
  );
};
